import _ from 'lodash';
import {
  CONTEXTUAL_TRIGGER_NAMES,
  CONTEXTUAL_EVENT_KEYS,
  CONTEXTUAL_TUTORIALS,
} from './constants';
import {
  type ContextualOnboardingConfigType,
  type ContextualOnboardingConfigItem,
} from './types';
import {CONTEXTUAL_EVENTS, CONTEXTUAL_EVENTS_LOOKUP} from './events';

const ALL_SITES_TRIGGERS: ContextualOnboardingConfigItem[] = [
  {
    id: CONTEXTUAL_TRIGGER_NAMES.ADD_LOCALES_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.ADD_LOCALES_CHECKLIST,
    description:
      'When a user manually triggers the add locales tutorial from the Get started checklist',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.DESIGNER_SETTINGS_PANEL_OPENED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.CLASS_UPDATED,
    tutorialId: CONTEXTUAL_TUTORIALS.CLASSES,
    description: 'When user creates or updates their first class',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.CLASS_UPDATED,
        seenCountTrigger: 2,
        specificity: 'more than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.FIRST_ELEMENT_ADDED,
    description: 'When the user adds their first element to any site',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.ELEMENT_ADDED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.FIRST_QS_ADDED,
    description: 'When the user adds their first quick stack to any site',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.QS_ADDED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.DISPLAY_SETTINGS_VIDEO_TUTORIAL_TRIGGERED,
    description:
      'When the user watches the Display Settings video tutorial triggered from the checklist',
    preReqs: [
      {
        eventId:
          CONTEXTUAL_EVENT_KEYS.DISPLAY_SETTINGS_VIDEO_TUTORIAL_TRIGGERED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.QUICK_STACK_VIDEO_TUTORIAL_TRIGGERED,
    description:
      'When the user watches the Quick Stack video tutorial triggered from the checklist',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.QUICK_STACK_VIDEO_TUTORIAL_TRIGGERED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.LIBRARIES_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.LIBRARIES,
    description: 'When a user opens the Designer with a Library installed',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.DESIGNER_OPENED_WITH_LIBRARY_INSTALLED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.STYLES_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.STYLES_CHECKLIST,
    description: 'When user clicks on the styles tutorial in the checklist',
    preReqs: [],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.VIDEO_TUTORIALS_TRIGGERED,
    description: 'When user opens video tutorials via left panel or checklist',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.VIDEO_TUTORIALS_TRIGGERED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.NINE_BOX_FLEX_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.FLEX_NINE_BOX,
    description: 'When user clicks on the Flex 9-box for the first time',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.NINE_BOX_FLEX_TUTORIAL_TRIGGERED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.NINE_BOX_GRID_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.GRID_NINE_BOX,
    description: 'When user clicks on the Grid 9-box for the first time',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.NINE_BOX_GRID_TUTORIAL_TRIGGERED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.AISG_THEME_CLICKED,
    description:
      'When the user first selects a theme pack in the AISG Theme panel',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.AISG_THEME_CLICKED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.AISG_THEME_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.AISG_THEME,
    description:
      'When the user clicks on the AISG Theme checklist item or first lands on AISG Home page',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.AISG_THEME_TUTORIAL_TRIGGERED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.AISG_PAGE_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.AISG_PAGE,
    description:
      'When the user clicks on the AISG New Page checklist item or first lands on AISG Home page and dissmisses the AISG Theme tutorial',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.AISG_PAGE_TUTORIAL_TRIGGERED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.AISG_FTU_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.AISG_FTU_TUTORIAL,
    description: 'When the existing user first lands on AISG Home page',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.AISG_FTU_TUTORIAL_TRIGGERED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.AISG_SECTION_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.AISG_SECTION,
    description: 'When the user clicks on the AISG New Section checklist item.',
    preReqs: [],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.AISG_HOVERED_SECTION_CONTROLS_CLICKED,
    description:
      'When the user first selects a theme pack in the AISG Theme panel',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.AISG_HOVERED_SECTION_CONTROLS_CLICKED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.PREVIEW_SITE_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.PREVIEW_SITE,
    description: 'When the user clicks on the Preview your site checklist item',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.PREVIEW_SITE_TUTORIAL_TRIGGERED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.ORION_SYNC_STYLES_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.ORION_SYNC_STYLES,
    description: 'When the user clicks on the Orion Sync Styles checklist item',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.ORION_SYNC_STYLES_TRIGGERED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.ORION_ADD_CONTEXT_POPOVER_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.ORION_ADD_CONTEXT_POPOVER,
    description: 'When the user clicks on the Orion Add Context Popover',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.ORION_ADD_CONTEXT_POPOVER_TRIGGERED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.ORION_SITE_ADD_SECTION_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.ORION_SITE_ADD_SECTION,
    description:
      'When the user clicks on the Orion Site Add Section checklist item',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.AIDA_ELEMENT_ADDED_TO_CANVAS,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.ORION_SITE_CHANGE_LAYOUT_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.ORION_SITE_CHANGE_LAYOUT,
    description:
      'When the user clicks on the Orion Site Change Layout checklist item',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.AISG_HOVERED_SECTION_CONTROLS_CLICKED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.ORION_SITE_GENERATE_PAGE_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.ORION_SITE_GENERATE_PAGE,
    description:
      'When the user clicks on the Orion Site Generate Page checklist item',
    preReqs: [
      {
        eventId:
          CONTEXTUAL_EVENT_KEYS.ORION_SITE_GENERATE_PAGE_TUTORIAL_TRIGGERED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.ORION_SITE_PUBLISH_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.ORION_SITE_PUBLISH,
    description:
      'When the user clicks on the Orion Site Publish checklist item',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.ORION_SITE_PUBLISH_TUTORIAL_TRIGGERED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.EDITOR_ROLE_ONBOARDING_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.EDITOR_ROLE_ONBOARDING,
    description:
      'First-time user experience for content editors on first designer entry',
    preReqs: [],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.MARKETER_ROLE_ONBOARDING_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.MARKETER_ROLE_ONBOARDING,
    description:
      'Manually triggered for new marketer-role users on first designer entry',
    preReqs: [],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.PAGES_PANEL_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.PAGES_PANEL_TUTORIAL,
    description:
      'When a marketer opens the designer in page build mode for the first time',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.PAGES_PANEL_VIEWED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.OPEN_CANVAS_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.OPEN_CANVAS_TUTORIAL,
    description:
      'When a user first sees the expand-canvas toggle with open canvas available',
    preReqs: [],
  },
];

const BLANK_SITE_ONLY_TRIGGERS: ContextualOnboardingConfigItem[] = [
  {
    id: CONTEXTUAL_TRIGGER_NAMES.CMS_BLANK_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.CMS_BLANK_CHECKLIST,
    description:
      'When user clicks on the CMS tutorial in the checklist (shown on blank sites)',
    preReqs: [],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.CLASSES_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.CLASSES_CHECKLIST,
    description:
      'When user clicks on the classes tutorial in the checklist (site specific checklist)',
    preReqs: [],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.SPACING_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.SPACING_CHECKLIST,
    description: 'When user clicks on the spacing tutorial in the checklist',
    preReqs: [],
  },
  {
    //
    id: CONTEXTUAL_TRIGGER_NAMES.CREATE_LAYOUT,
    tutorialId: CONTEXTUAL_TUTORIALS._EXPERIMENTAL_ADD_ELEMENTS_CHECKLIST,
    description:
      'When a user clicks on the Create Layout tutorial in the checklist',
    preReqs: [],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.CREATE_LAYOUT_QS,
    tutorialId: CONTEXTUAL_TUTORIALS._CHECKLIST_DRAG_QS,
    description:
      'When a user clicks on the Add a Layout tutorial in the checklist',
    preReqs: [],
  },
];

const TEMPLATE_SITE_ONLY_TRIGGERS: ContextualOnboardingConfigItem[] = [
  {
    id: CONTEXTUAL_TRIGGER_NAMES.CMS_TEMPLATE_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.CMS_TEMPLATE_CHECKLIST,
    description:
      'When user clicks on the CMS tutorial in the checklist (shown on template sites)',
    preReqs: [],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.IMAGES_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.IMAGES_CHECKLIST,
    description:
      'When user clicks on the image replacement tutorial in the checklist',
    preReqs: [],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.NAVIGATOR_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.NAVIGATOR_CHECKLIST,
    description:
      'When user clicks on the navigator tutorial in the checklist for templates',
    preReqs: [],
  },
  {
    id: CONTEXTUAL_TRIGGER_NAMES.ASSETS_UPLOADED,
    description: 'When the user uploads assets',
    preReqs: [
      {
        eventId: CONTEXTUAL_EVENT_KEYS.ASSETS_UPLOADED,
        seenCountTrigger: 1,
        specificity: 'less than',
      },
    ],
  },
];

const EXPERIMENTAL_TRIGGERS: ContextualOnboardingConfigItem[] = [
  {
    id: CONTEXTUAL_TRIGGER_NAMES.LAYOUT_ELEMENT_TUTORIAL_TRIGGERED,
    tutorialId: CONTEXTUAL_TUTORIALS.LAYOUT_ELEMENT,
    description:
      'When a user clicks on the Quick Stack tutorial in the Quick Stack Presets Popover',
    preReqs: [],
  },
];

export const CONTEXTUAL_ONBOARDING_TRIGGERS_ITEMS: ContextualOnboardingConfigItem[] =
  [
    ...ALL_SITES_TRIGGERS,
    ...BLANK_SITE_ONLY_TRIGGERS,
    ...TEMPLATE_SITE_ONLY_TRIGGERS,
    ...EXPERIMENTAL_TRIGGERS,
  ];

export const CONTEXTUAL_ONBOARDING_ITEMS_LOOKUP: ContextualOnboardingConfigType =
  CONTEXTUAL_ONBOARDING_TRIGGERS_ITEMS.reduce<Record<string, any>>(
    (item, current) => ({...item, [current.id]: current}),
    {}
  );

const allAnalyticsEventListeners = _.uniq(
  _.flatten(CONTEXTUAL_ONBOARDING_TRIGGERS_ITEMS.map((item) => item.preReqs)) // grab all event configurations
    .map((event) =>
      event.eventId
        ? CONTEXTUAL_EVENTS_LOOKUP[event.eventId]?.analyticsEventListener
        : undefined
    ) // grab the name of the analytics event
    .filter(Boolean) // filter undefined
);

export const contextualEventLookupByAnalyticsEvent =
  allAnalyticsEventListeners.reduce<Record<string, any>>(
    (lookup, eventName) => ({
      ...lookup,
      [eventName as string]: CONTEXTUAL_EVENTS.filter(
        (event) => event.analyticsEventListener === eventName
      ),
    }),
    {}
  );
